import type { NPC, Quest } from '../../../../shared/NPCData';
import type { TomeTone } from '../../primitives/tome';

export interface QuestGroup {
  id: 'ready' | 'available' | 'active' | 'completed';
  title: string;
  tone: TomeTone;
  variant: 'full' | 'compact';
  quests: Quest[];
}

/** One slot of the NPC quest page, top to bottom. */
export type QuestViewSlot =
  | { kind: 'folio'; quest: Quest }
  | { kind: 'teaser'; quest: Quest }
  | { kind: 'group'; group: QuestGroup };

const isReady = (q: Quest) => q.isTurnInReady && !q.isCompleted;

/**
 * The single ordering rule for the NPC quest page (plan 13 §2, DJ: "turn-ins
 * should always be on top of new accepts or other dialogue"):
 *
 *   1. ready to turn in — featured folios first, then the ordinary chapter
 *   2. featured offers / featured work in progress
 *   3. locked featured teasers
 *   4. available, 5. in progress, 6. completed (ordinary chapters)
 *
 * Empty groups are dropped. A featured quest that is completed is not shown.
 */
export function questViewOrder(npc: Pick<NPC, 'quests' | 'featuredTeasers'>): QuestViewSlot[] {
  const featured = npc.quests.filter(q => q.featured && !q.isCompleted);
  const teasers = npc.featuredTeasers ?? [];
  const ordinary = npc.quests.filter(q => !featured.includes(q));
  const otherFeatured = featured.filter(q => !isReady(q));
  // A featured offer leads the offers: ordinary offers fold their story and map beneath it.
  const offerVariant = otherFeatured.length + teasers.length > 0 ? 'compact' : 'full';
  const group = (g: QuestGroup): QuestViewSlot[] => g.quests.length > 0 ? [{ kind: 'group', group: g }] : [];
  return [
    ...featured.filter(isReady).map(quest => ({ kind: 'folio' as const, quest })),
    ...group({ id: 'ready', title: 'Ready to turn in', tone: 'ready', variant: 'compact', quests: ordinary.filter(isReady) }),
    ...otherFeatured.map(quest => ({ kind: 'folio' as const, quest })),
    ...teasers.map(quest => ({ kind: 'teaser' as const, quest })),
    ...group({ id: 'available', title: 'Available', tone: 'default', variant: offerVariant, quests: ordinary.filter(q => !q.isAccepted && !q.isCompleted) }),
    ...group({ id: 'active', title: 'In progress', tone: 'default', variant: 'compact', quests: ordinary.filter(q => q.isAccepted && !q.isTurnInReady && !q.isCompleted) }),
    ...group({ id: 'completed', title: 'Completed', tone: 'quiet', variant: 'compact', quests: ordinary.filter(q => q.isCompleted) }),
  ];
}
